import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import { CheckCircle, Loader2, XCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const { fetchUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading"); // loading, success, error
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setStatus("error");
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempt = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempt >= maxAttempts) {
      setStatus("error");
      return;
    }

    setAttempts(attempt + 1);

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      
      if (response.data.payment_status === "paid") {
        setStatus("success");
        await fetchUser();
        toast.success("Payment successful! Welcome to the SHRED program!");
      } else if (response.data.status === "expired") {
        setStatus("error");
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempt + 1), pollInterval);
      }
    } catch (error) {
      console.error("Error checking payment status:", error);
      setTimeout(() => pollPaymentStatus(sessionId, attempt + 1), pollInterval);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-md"
      >
        <Card className="bg-card border-border">
          <CardContent className="p-8 text-center">
            {status === "loading" && (
              <>
                <Loader2 className="h-16 w-16 text-primary animate-spin mx-auto mb-6" />
                <h2 className="text-2xl font-black uppercase tracking-tighter mb-2">Processing Payment...</h2>
                <p className="text-muted-foreground mb-4">Please wait while we confirm your payment.</p>
                <div className="text-sm text-muted-foreground">Attempt {attempts} of 5</div>
              </>
            )}

            {status === "success" && (
              <>
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", delay: 0.2 }}
                >
                  <CheckCircle className="h-16 w-16 text-accent mx-auto mb-6" />
                </motion.div>
                <h2 className="text-2xl font-black uppercase tracking-tighter mb-2">WELCOME TO THE SHRED!</h2>
                <p className="text-muted-foreground mb-6">
                  Your payment was successful. Your 60-day transformation begins now.
                </p>
                <Button
                  data-testid="payment-success-dashboard-btn"
                  onClick={() => navigate("/dashboard")}
                  className="w-full bg-primary hover:bg-primary/90 uppercase font-bold tracking-widest rounded-sm"
                >
                  Go to Dashboard
                </Button>
              </>
            )}

            {status === "error" && (
              <>
                <XCircle className="h-16 w-16 text-destructive mx-auto mb-6" />
                <h2 className="text-2xl font-black uppercase tracking-tighter mb-2">Payment Issue</h2>
                <p className="text-muted-foreground mb-6">
                  We couldn't verify your payment. Please try again or contact support.
                </p>
                <div className="space-y-3">
                  <Button
                    onClick={() => navigate("/pricing")}
                    className="w-full bg-primary hover:bg-primary/90 uppercase font-bold tracking-widest rounded-sm"
                  >
                    Try Again
                  </Button>
                  <Button
                    onClick={() => navigate("/dashboard")}
                    variant="outline"
                    className="w-full uppercase font-bold tracking-widest rounded-sm"
                  >
                    Go to Dashboard
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
